import type {
  FreeField,
  MultipleChoiceFilter,
  SingleChoiceFilter,
} from 'generated/resources/datatable.graphql'

import { ID_FILTER_KINDS } from 'modules/core/components/DataTable/constants'
import type { Filter as FilterType } from 'modules/core/components/DataTable/types'
import Filter from 'modules/core/components/Filter'

import FolderChoice from 'modules/documents/lib/datatable/documents/filters/_choices/FolderChoice'

import Header from './Header'

type Props<T> = {
  filter: FilterType<T>
  onValueChange: (value: unknown) => void
  onCategoryChange: (category: FilterType<T>['category']) => void
  defaultOpen?: boolean
}

export default function IdFilter<T>({
  filter: dataTableFilter,
  onValueChange,
  onCategoryChange,
  defaultOpen,
}: Props<T>) {
  const { filter, category, value } = dataTableFilter

  const showHeader = filter.categories.length > 1
  const header = showHeader && (
    <Header filter={filter} category={category} onCategoryChange={onCategoryChange} />
  )

  const commonProps = {
    label: filter.title,
    onValueChange,
    header,
  }

  switch (category.kind) {
    case ID_FILTER_KINDS.MULTIPLE_CHOICE:
      return (
        <Filter.MultipleChoice
          {...commonProps}
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          options={(category as MultipleChoiceFilter<string>).options!}
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          value={value as MultipleChoiceFilter<string>['value']}
          defaultOpen={defaultOpen}
          hideSearch={category.hideSearch}
        />
      )
    case ID_FILTER_KINDS.SINGLE_CHOICE:
      return (
        <Filter.SingleChoice
          {...commonProps}
          options={category.options}
          value={value as SingleChoiceFilter<string>['value']}
          defaultOpen={defaultOpen}
        />
      )
    // FIXME: This should be a new kind of CUSTOM_FILTER
    case ID_FILTER_KINDS.DOCUMENTS_FOLDER_CHOICE:
      return (
        <FolderChoice
          {...commonProps}
          options={category.options}
          value={value as string}
          defaultOpen={defaultOpen}
        />
      )
    case ID_FILTER_KINDS.FREE_FIELD:
      return (
        <Filter.FreeField
          value={value as FreeField<string>['value']}
          description={category.description}
          errorMessage={category.errorMessage}
          options={category.options}
          validation={category.validation}
          defaultOpen={defaultOpen}
          {...commonProps}
          fieldType="string"
        />
      )
  }

  throw new Error(`Unknown filter kind: ${category.kind}`)
}
